/**
 * Constraint Panel Component
 * 
 * Allows leaders to apply system-wide constraints that influence the Krysalis Core.
 * Part of the Control Bridge control surface.
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - "Control Bridge" definition
 */

import React, { useState } from 'react';
import { Switch, Typography, Tooltip, Badge } from 'antd';
import {
  LockOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import styles from './ConstraintPanel.module.css';

const { Text } = Typography;

export interface Constraint {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
  severity: 'info' | 'warning' | 'critical';
}

interface ConstraintPanelProps {
  /** Constraints to display */
  constraints?: Constraint[];
  /** Callback when a constraint is toggled */
  onConstraintToggle?: (constraintId: string, enabled: boolean) => void;
  /** Whether in compact mode */
  compact?: boolean;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

const DEFAULT_CONSTRAINTS: Constraint[] = [
  {
    id: 'block-high-risk',
    label: 'Block High-Risk Transitions',
    description: 'Prevent state transitions marked as high-risk without approval',
    icon: <AlertOutlined />,
    enabled: true,
    severity: 'critical',
  },
  {
    id: 'require-approval',
    label: 'Require Priority Approval',
    description: 'Changes to domain priorities require leadership approval',
    icon: <SafetyOutlined />,
    enabled: false,
    severity: 'warning',
  },
  {
    id: 'enforce-sequence',
    label: 'Enforce Signal Sequence',
    description: 'Execution signals must be resolved in order',
    icon: <ClockCircleOutlined />,
    enabled: true,
    severity: 'info',
  },
  {
    id: 'lock-critical-artifacts',
    label: 'Lock Critical Artifacts',
    description: 'Prevent modifications to artifacts marked as critical',
    icon: <LockOutlined />,
    enabled: false,
    severity: 'critical',
  },
];

const getSeverityColor = (severity: Constraint['severity']) => {
  switch (severity) {
    case 'critical':
      return '#ef4444';
    case 'warning':
      return '#f59e0b';
    case 'info':
    default:
      return '#3b82f6';
  }
};

export const ConstraintPanel: React.FC<ConstraintPanelProps> = ({
  constraints = DEFAULT_CONSTRAINTS,
  onConstraintToggle,
  compact = false,
  disabled = false,
  className,
}) => {
  const [localConstraints, setLocalConstraints] = useState<Constraint[]>(constraints);

  const handleToggle = (constraintId: string, enabled: boolean) => {
    setLocalConstraints(prev =>
      prev.map(c => (c.id === constraintId ? { ...c, enabled } : c))
    );
    onConstraintToggle?.(constraintId, enabled);
  };

  const activeCount = localConstraints.filter(c => c.enabled).length;

  const containerClasses = [
    styles.container,
    compact && styles.compact,
    disabled && styles.disabled,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <LockOutlined className={styles.headerIcon} />
          <Text className={styles.title}>Active Constraints</Text>
        </div>
        <Badge
          count={activeCount}
          className={styles.activeCount}
          style={{ backgroundColor: activeCount > 0 ? '#10b981' : '#6b7280' }}
        />
      </div>

      <div className={styles.constraintsList}>
        {localConstraints.map(constraint => (
          <div key={constraint.id} className={styles.constraintItem}>
            <div className={styles.constraintInfo}>
              <div className={styles.constraintHeader}>
                <span
                  className={styles.constraintIcon}
                  style={{ color: getSeverityColor(constraint.severity) }}
                >
                  {constraint.icon}
                </span>
                <Text className={styles.constraintLabel}>{constraint.label}</Text>
                {!compact && (
                  <Tooltip title={constraint.description}>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                )}
              </div>
              {!compact && (
                <Text type="secondary" className={styles.constraintDesc}>
                  {constraint.description}
                </Text>
              )}
            </div>
            <Switch
              checked={constraint.enabled}
              onChange={checked => handleToggle(constraint.id, checked)}
              disabled={disabled}
              checkedChildren={<CheckCircleOutlined />}
              unCheckedChildren={<LockOutlined />}
              className={styles.constraintSwitch}
            />
          </div>
        ))}
      </div>

      {!compact && (
        <div className={styles.footer}>
          <Text type="secondary" className={styles.footerText}>
            Constraints enforce rules across the entire system and cannot be bypassed by individual domains.
          </Text>
        </div>
      )}
    </div>
  );
};

export default ConstraintPanel;
